import { createContext, useMemo } from "react"

import { BehaviorSubject } from "rxjs"

import NotificationContactData from "../../../api/Randoms/Notification/NotificationContactData"
import NotificationContactsGetParams from "../../../api/Randoms/Notification/NotificationContactsGetParams"
import NotificationGridData from "../../../api/Randoms/Notification/NotificationGridData"
import useNotificationContactsDelete from "../../../api/Randoms/Notification/useNotificationContactsDelete"
import useNotificationContactsLoad from "../../../api/Randoms/Notification/useNotificationContactsLoad"
import useNotificationsSend from "../../../api/Randoms/Notification/useNotificationsSend"

interface NotificationContactsProviderProps {
  poolId: number
  children: React.ReactNode
}

interface NotificationContactsContextType {
  poolId: number
  gridData: NotificationGridData
  selectedContacts: NotificationContactData[]
  loadingContacts: boolean
  deletingContacts: { [key: number]: boolean }
  sendingNotifications: { [key: number]: boolean }
}

interface NotificationContactsActionsContextType {
  page$: BehaviorSubject<NotificationContactsGetParams>
  deleteContacts: (poopId: number, contact: NotificationContactData[]) => void
  sendNotifications: (poopId: number, contact: NotificationContactData[]) => void
  selectContacts: (contactIds: number[], checked: boolean) => void
  reloadContacts: () => void
}

export const NotificationContactsContext = createContext<NotificationContactsContextType | null>(null)
export const NotificationContactsActionsContext = createContext<NotificationContactsActionsContextType | null>(null)

function NotificationContactsProvider({ poolId, children }: NotificationContactsProviderProps) {
  const { loadingContacts, gridData, selectedContacts, page$, selectContacts, reloadContacts } =
    useNotificationContactsLoad(poolId)
  const { deletingContacts, deleteContacts } = useNotificationContactsDelete(reloadContacts)
  const { sendingNotifications, sendNotifications } = useNotificationsSend(reloadContacts)

  const value = useMemo(
    () => ({
      poolId,
      gridData,
      selectedContacts,
      loadingContacts,
      deletingContacts,
      sendingNotifications,
    }),
    [poolId, gridData, selectedContacts, loadingContacts, deletingContacts, sendingNotifications]
  )

  const actions = useMemo(
    () => ({
      page$,
      deleteContacts,
      sendNotifications,
      selectContacts,
      reloadContacts,
    }),
    [page$, deleteContacts, reloadContacts, selectContacts, sendNotifications]
  )

  return (
    <NotificationContactsContext.Provider value={value}>
      <NotificationContactsActionsContext.Provider value={actions}>
        {children}
      </NotificationContactsActionsContext.Provider>
    </NotificationContactsContext.Provider>
  )
}

export default NotificationContactsProvider
